
// @ts-nocheck
import { defineComponent, reactive, ref, computed, onMounted, getCurrentInstance } from 'vue';
import { useUserActions, useToolActions, useUserState } from '@/store';
import axios from 'axios';
import frameworks from '@/data/frameworks.json';
import { pushToAuth } from '@/router/redirect';
import {
  saveAutomationWizardState,
  loadAutomationWizardState,
  clearAutomationWizardState
} from '@/services/secureStorage/automationStorage';
import SetupScopesWizard from './SetupScopesWizard.vue';
import SetupSiteWizard from './SetupSiteWizard.vue';
// import useWebSocket from './wsManager';
// import SetupProgramWizard from './SetupProgramWizard.vue';

export default defineComponent({
  name: 'SetupWizard',
  components: {
    'setup-site': SetupSiteWizard,
    'setup-scopes': SetupScopesWizard
    // 'setup-program': SetupProgramWizard,
  },
  props: {
    enabled: {
      type: Boolean,
      required: false,
      default: false
    },
    programDoc: {
      type: Object,
      required: false,
      default: () => {}
    },
    defaultOpen: {
      type: Boolean,
      default: false
    },
    isDisplay: {
      type: Boolean,
      default: false
    }
  },
  setup(props: any, ctx: any) {
    const { user }: any = useUserState(['user']);
    const { createAutomationProgram, simulateScope, simulateProgramDetails, updateAutomationAdk } =
      useUserActions([
        'createAutomationProgram',
        'simulateScope',
        'simulateProgramDetails',
        'updateAutomationAdk'
      ]);
    // const authToken = localStorage.getItem('apollo-token');
    // const { getUserType } = useUserGetters(['getUserType']);
    // const defaultOpenCondition = ref(
    //   Boolean(
    //     !props.programDoc.data.adks
    //       .find(adk => adk?.name === 'start')
    //       .fields.find(field => field.model === 'programDesc').setup.programDesc
    //   )
    // );
    const instance = getCurrentInstance();
    const defaultOpenCondition = ref(true);
    if (
      !props?.programDoc?.data?.adks
        ?.find(adk => adk?.name === 'start')
        ?.fields?.find(field => field?.model === 'programDesc')?.setup?.programDesc
    ) {
      defaultOpenCondition.value = false;
    }
    const modalToggled = ref(props.defaultOpen && props.enabled && defaultOpenCondition?.value);
    const automateSetupModal = computed<boolean>({
      get: () => {
        return modalToggled?.value;
      },
      set: value => {
        modalToggled.value = Boolean(value);
      }
    });

    const stage = computed(() => {
      switch (process.env.VUE_APP_NODE_ENV) {
        case 'development':
          return 'dev';
        case 'stage':
          return 'stage';
        case 'production':
          return 'prod';
        default:
          return 'dev';
      }
    });
    // const authToken = localStorage.getItem('apollo-token');
    // const wizardURL = `wss://automate-setup-service-${stage.value}.ws.pilotcity.com/`;
    // const socket = useWebSocket({ authToken: `${authToken}`, url: wizardURL });
    enum SETUP_STATE {
      'SITE',
      'SCOPE'
    }
    const setupState = ref(SETUP_STATE.SITE);
    const projectScopes = ref<any[]>([]);
    const website = ref('');
    const selectedScope = ref('');

    /*
     * Restore wizard state if the user was redirected to auth and came back.
     * We persist the website URL, generated scopes array and the scope the
     * user selected in localStorage before redirecting.  If those keys are
     * present we hydrate the reactive state so the modal reopens on the
     * Scope step with the previous selection highlighted.
     */
    (function restorePersistedWizardState() {
      const persisted = loadAutomationWizardState();
      if (!persisted) return;
      website.value = persisted.website || '';
      projectScopes.value = persisted.scopes || [];
      selectedScope.value = persisted.selectedScope || '';

      // If a scope was already selected, jump directly to the Scope step
      if (selectedScope.value) {
        setupState.value = SETUP_STATE.SCOPE;
      }
    })();
    const newProgramData = reactive({});
    const siteLoading = ref(false);
    const programLoading = ref(false);
    const errors = ref(false);
    const finishedUpdate = ref(false);
    const impersonateUserId = ref('');
    const root = instance?.proxy;

    // Add form state
    const formState = reactive({
      role: '',
      subject: '',
      location: '',
      customLocation: ''
    });

    // Add subject options
    interface SubjectOption {
      text: string;
      value: string;
    }

    const currentSubjectOptions = computed<SubjectOption[]>(() => {
      const uniqueCourses = new Set<string>();
      frameworks.frameworks.forEach(framework => {
        if (framework.course_name) {
          uniqueCourses.add(framework.course_name);
        }
      });
      return Array.from(uniqueCourses)
        .sort((a, b) => a.localeCompare(b))
        .map(course => ({
          text: course,
          value: course.toLowerCase().replace(/\s+/g, '_')
        }));
    });

    function openModal() {
      automateSetupModal.value = true;
      errors.value = false;
      siteLoading.value = false;
      programLoading.value = false;
      errors.value = false;
      // socket.connect();
    }
    onMounted(() => {
      if (props?.isDisplay) {
        openModal();
      }
      // if (automateSetupModal.value) socket.connect();
    });
    function closeModal() {
      automateSetupModal.value = false;
      website.value = '';
      projectScopes.value = [];
      errors.value = false;
      // socket.disconnect();
      setTimeout(() => resetSetupState(), 100);
      ctx.emit('close');
      // Clean up persisted wizard data
      clearAutomationWizardState();
    }
    async function submitSiteHandler(data) {
      siteLoading.value = true;

      // Handle both string and object inputs
      let sitesArray = [];
      let scopesData = [];

      if (typeof data === 'string') {
        // Handle single website case
        if (!data) {
          alertUser('Please enter a valid website url to continue', 'error');
          siteLoading.value = false;
          return;
        }
        sitesArray = [data];
      } else if (data.websites) {
        // Handle multiple websites from OpenAI response
        sitesArray = data.websites
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);

        // If we have scopes data, store it
        if (data.scopes && Array.isArray(data.scopes)) {
          scopesData = data.scopes;
          projectScopes.value = data.scopes;
        }
      }

      // Store the full URL
      website.value = sitesArray.join(', ');

      // If we have multiple sites, navigate to bulk setup
      if (sitesArray.length > 1) {
        const currentRoute = instance?.proxy?.$route;
        const newQuery = { ...currentRoute.query };
        newQuery.sites = JSON.stringify(sitesArray);
        if (scopesData.length > 0) {
          newQuery.scopes = JSON.stringify(scopesData);
        }
        instance?.proxy?.$router.push({
          path: '/bulk-scope-setup',
          query: newQuery
        });
        return;
      }

      // For single site, proceed with normal flow
      await requestProgramScopes();

      siteLoading.value = false;
      if (projectScopes.value && projectScopes.value.length > 0) incrementState();
    }

    const signIn = async (email, password) => {
      const signInData: any = {
        type: 'impersonate_signin',
        email,
        password
      };
      const url = `${process.env.VUE_APP_AUTH0}/sign-in`;

      try {
        const resp = await axios.post(url, signInData);
        if (resp && resp.status === 200) {
          impersonateUserId.value = resp.data.data.userId;
        } else {
          window.location.href = '/';
        }

        return false;
      } catch (err: any) {
        if (err.response && (err.response.status === 400 || err.response.status === 403)) {
          const errorData = err.response.data;
          window.location.href = '/';
        }
        return false;
      }
    };

    async function submitScopeHandler() {
      if (user?.value) {
        if (
          !user?.value?.userTypes?.includes('Employer') &&
          !user?.value?.userTypes?.includes('Teacher') &&
          !user?.value?.userTypes?.includes('Student')
        ) {
          alertUser('User must be host to create automation program!', 'error');
          automateSetupModal.value = false;
        } else {
          localStorage.removeItem('customSelectedScope');
          programLoading.value = true;
          const websiteValue = website.value;
          const scope = selectedScope.value;
          if (!scope) {
            alertUser('Please select a program before continuing', 'error');
            programLoading.value = false;
            return;
          }
          const queryParams = new URLSearchParams(window.location.search);
          if (queryParams.has('email') && queryParams.has('password')) {
            const signInResponse = await signIn(
              queryParams.get('email'),
              queryParams.get('password')
            );
          }
          const data = {
            companySite: websiteValue,
            programScope: scope
          };
          // ctx.emit('openCreateProgram', data);
          await requestProgramDetails(data).then(() => requestProgramUpdate());
          if (!finishedUpdate.value) {
            alertUser('Something went wrong', 'error');
            return;
          }
          closeModal();
          if (props?.programDoc?.data) {
            // eslint-disable-next-line no-restricted-globals
            location.reload();
          }
          // const { fetchProgramById } = useUserActions(['fetchProgramById']);
          // await fetchProgramById({ program_id: props.programDoc.data._id });
        }
      } else {
        // Only open onboarding if the user still needs to complete their profile
        const targetPath: string = root.$route.fullPath || root.$route.path;
        pushToAuth(root.$router, targetPath);

        // Persist wizard context so we can restore after login/signup
        saveAutomationWizardState({
          website: website.value,
          scopes: projectScopes.value,
          selectedScope: selectedScope.value
        });
        // localStorage.setItem('isAutomationRoute', 'true');
      }
    }
    /** BEGIN API I/O */
    async function requestProgramScopes() {
      const websiteValue = website.value;
      const quantity = 6; // default
      try {
        // const res: any = await socket.sendMessage({
        //   action: 'scope',
        //   data: {
        //     companySite: websiteValue,
        //     scopeQty: quantity
        //   }
        // });
        const res = await simulateScope({
          companySite: websiteValue,
          scopeQty: quantity
        });
        // ADD: Guard against undefined/null or non-iterable responses
        if (Array.isArray(res) && res.length) {
          projectScopes.value.push(...res);
        } else {
          throw new Error('Unable to generate program scopes for this site');
        }
      } catch (err) {
        const notFoundRegex = /Apollo data not found/;
        const openAIRegex = /Something went wrong with scope completion/;
        const isAIError = openAIRegex.test(err.message);
        const isNotFound = notFoundRegex.test(err.message);
        if (isNotFound)
          alertUser(
            'Unable to retrieve data for this site, please check spelling. Otherwise, we are unable to automatically generate a program for this organization at this time.',
            'error'
          );
        else if (isAIError)
          alertUser(
            'ERROR: Unable to generate projects at this time, Please try again later.',
            'error'
          );
        else {
          alertUser(`${err.message}, please try again`, 'error');
          automateSetupModal.value = false;
        }
        console.error('Error with requesting scopes: ', err);
        siteLoading.value = false;
      }
    }
    const modifyProgramName = (programName: string) => {
      // const epochTime = new Date().getTime();
      // return `${programName}_${epochTime}`;
    };
    async function requestProgramUpdate() {
      const newProgram = { ...newProgramData };
      try {
        if (newProgramData !== 'You are unable to create program') {
          newProgram.programType = 'automation';
          if (props?.programDoc?.data) {
            const { _id: programId } = props.programDoc.data;
            console.warn(programId, newProgram.adks);
            // await socket.sendMessage({
            //   action: 'adks',
            //   data: {
            //     programId,
            //     adks: newProgram.adks
            //   }
            // });
            await updateAutomationAdk({
              programId,
              adks: newProgram.adks
            });
          } else {
            const queryParams = new URLSearchParams(window.location.search);
            queryParams.get('id');
            newProgram.userId =
              queryParams.has('email') && queryParams.has('password')
                ? impersonateUserId.value.toString()
                : user?.value?._id?.toString();
            // Emit event so landing page opens template selection modal
            ctx.emit('openCreateProgram', newProgram);
          }
        } else {
          alertUser(newProgramData, 'error');
        }
      } catch (err) {
        console.error('Error while trying to update program: ', err);
        programLoading.value = false;
      }
      finishedUpdate.value = true;
    }
    async function requestProgramDetails(data) {
      try {
        // const res = await socket.sendMessage({
        //   action: 'details',
        //   data
        // });
        const res = await simulateProgramDetails(data);
        Object.assign(newProgramData, res);
      } catch (err) {
        console.error('Error while trying to generate program: ', err);
        const messageRegex = /^(?!.*error).*\n(.*)/i;
        const match = err.message.match(messageRegex);
        const output = match[1];
        alertUser(output, 'error');
      }
    }
    /** END API I/O */
    /** BEGIN HELPERS */
    async function alertUser(message, type) {
      if (type === 'error') errors.value = true;
      const { showSnackbar } = useToolActions(['showSnackbar']);
      showSnackbar({
        message,
        type,
        isShowSnackbar: true
      });
    }
    function incrementState() {
      errors.value = false;
      if (setupState.value === SETUP_STATE.SITE) setupState.value = SETUP_STATE.SCOPE;
      else if (setupState.value === SETUP_STATE.SCOPE) setupState.value = SETUP_STATE.SCOPE;
      else automateSetupModal.value = false;
    }
    function resetSetupState() {
      setupState.value = SETUP_STATE.SITE;
    }

    const outSideClick = event => {
      // socket.disconnect(event);
      ctx.emit('close');
    };
    /** END HELPERS */

    // Add new error handlers
    function handleNoResults(message) {
      alertUser(message, 'warning');
      siteLoading.value = false;
    }

    function handleSearchError(message) {
      alertUser(message, 'error');
      siteLoading.value = false;
    }

    return {
      website,
      modalToggled,
      newProgramData,
      programLoading,
      siteLoading,
      setupState,
      projectScopes,
      automateSetupModal,
      selectedScope,
      SETUP_STATE,
      errors,
      submitSiteHandler,
      submitScopeHandler,
      requestProgramDetails,
      requestProgramScopes,
      requestProgramUpdate,
      finishedUpdate,
      alertUser,
      incrementState,
      resetSetupState,
      closeModal,
      openModal,
      handleNoResults,
      handleSearchError,
      formState,
      currentSubjectOptions
    };
  }
});
